import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Cell, Pie, PieChart, Tooltip as RTooltip } from 'recharts';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import * as R from 'ramda';
import { shape } from 'prop-types';

import CustomMinTooltip from './CustomMinTooltip';

const MinDonut = ({ data }) => {
  const maxAngle = 270;
  const getSome = arr => arr.reduce((a, b) => a + b, 0);
  const totals = getSome(R.path(['projections', 'total'], data).map(({ value }) => value));
  const overAllocation = R.prop('overAllocation', data);
  const underAllocation = R.prop('underAllocation', data);

  const calcAngle = () => {
    const maxVol = getSome(R.path(['projections', 'required'], data).map(({ value }) => value));
    const percent = maxAngle * (totals / maxVol);
    return maxAngle - percent;
  };

  const calcFill = R.cond([
    [R.equals('Min'), R.always('#213657')],
    [R.equals('Min Required'), R.always('#213657')],
    [R.equals('Contract Min'), R.always('rgba(33, 54, 87, 0.25)')],
    [R.equals('Min Volume'), R.always('#22883A')],
    [R.equals('Min Projection'), R.always('#22883A')],
  ]);

  return (
    <Box sx={{ position: 'relative', width: 344 }}>
      <PieChart height={344} width={344}>
        <Pie
          cx={172}
          cy={172}
          data={R.reverse(R.path(['projections', 'required'], data))}
          dataKey="value"
          endAngle={0}
          innerRadius={135}
          outerRadius={165}
          paddingAngle={2}
          startAngle={maxAngle}
        >
          {R.reverse(R.path(['projections', 'required'], data)).map(({ name }) => (
            <Cell fill={calcFill(name)} key={name} />
          ))}
        </Pie>
        <Pie
          cx={172}
          cy={172}
          data={R.reverse(R.path(['projections', 'total'], data))}
          dataKey="value"
          endAngle={calcAngle()}
          innerRadius={95}
          outerRadius={125}
          paddingAngle={2}
          startAngle={maxAngle}
        >
          {R.reverse(R.path(['projections', 'total'], data)).map(({ name }) => (
            <Cell
              fill={underAllocation ? '#B71C1C' : overAllocation ? '#F57F17' : calcFill(name)}
              key={name}
            />
          ))}
        </Pie>
        <RTooltip
          content={<CustomMinTooltip />}
          tooltipData={R.prop('tooltip', data)}
          wrapperStyle={{ zIndex: 1000 }}
        />
      </PieChart>
      <Stack
        sx={{
          height: 134,
          left: '27%',
          p: 1,
          position: 'absolute',
          top: '34%',
          width: 170,
        }}
      >
        <Stack alignItems="center">
          <Typography variant="subtitle2">Current Total Vol:</Typography>
          <Typography
            color={overAllocation || underAllocation ? 'error.main' : 'text.primary'}
            variant="h6"
          >
            {formatNumberWithoutDecimals(totals)} bbl
          </Typography>
          {overAllocation && (
            <Stack alignItems="center">
              <Typography color="text.primary" variant="caption">
                Excess Volume
                <Tooltip
                  placement="bottom"
                  slotProps={{
                    popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
                  }}
                  title="Volume is calculated with the following: Min. System Vol - Current Minimum"
                >
                  <InfoRoundedIcon
                    sx={{
                      '&:hover': { cursor: 'pointer' },
                      color: 'graphic.dark',
                      fontSize: 16,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography color="error.main" variant="caption">
                {formatNumberWithoutDecimals(R.prop('excessVolume', data))} bbl
              </Typography>
            </Stack>
          )}
          {underAllocation && !overAllocation && (
            <Stack alignItems="center">
              <Typography color="text.primary" variant="caption">
                Additional Vol. Needed
                <Tooltip
                  placement="bottom"
                  slotProps={{
                    popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
                  }}
                  title="Volume is calculated with the following: Min. System Vol - Current Minimum"
                >
                  <InfoRoundedIcon
                    sx={{
                      '&:hover': { cursor: 'pointer' },
                      color: 'graphic.dark',
                      fontSize: 16,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography color="error.main" variant="caption">
                {formatNumberWithoutDecimals(R.prop('additionalVolumeNeeded', data))} bbl
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack alignItems="center">
          <Typography color="text.primary" variant="caption">
            Current Minimum:
            <Tooltip
              placement="bottom"
              slotProps={{
                popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
              }}
              title="Current Min is updated on the 8th of each month from a calculation made on the 1st of each month"
            >
              <InfoRoundedIcon
                sx={{
                  '&:hover': { cursor: 'pointer' },
                  color: 'graphic.dark',
                  fontSize: 16,
                  mt: 0.5,
                }}
              />
            </Tooltip>
          </Typography>
          <Typography variant="body2">
            {formatNumberWithoutDecimals(R.prop('currentMin', data))} bbl
          </Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{ fontWeight: 700, position: 'absolute', right: 0, top: '49%' }}
        variant="body2"
      >
        ---------
      </Typography>
      <Typography
        sx={{ bottom: 45, fontWeight: 700, left: '54%', position: 'absolute' }}
        variant="body1"
      >
        Min Total
      </Typography>
      <Typography
        sx={{ bottom: 6, fontWeight: 700, left: '54%', position: 'absolute' }}
        variant="body1"
      >
        Min Capacity
      </Typography>
    </Box>
  );
};

MinDonut.propTypes = {
  data: shape({}),
};

MinDonut.defaultProps = {
  data: null,
};

export default MinDonut;
